/* eslint-disable default-case */
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Skeleton from 'react-loading-skeleton'
import { trackEvent } from 'analytics/trackFunctions'
import { MeContext } from 'contexts/me'
import { URL_AUTOGESTION, URL_RENOVADOR } from 'config'
import { hasActiveMembership } from 'utils'
import { getCreditLinesSummary, CREDIT_PRODUCT } from 'api/advances'
import { toLocaleCurrency } from 'utils/dinero'
import { LineSummary } from 'types/api/loan'
import { UserContext } from 'contexts/user'
import delayedLoan from 'assets/images/loan-delayed.webp'
import ErrorIcon from 'assets/icons/ErrorIcon'
import notLendMoneyImg from 'assets/images/not-lend-money.webp'
import { HelpIcon } from 'assets/icons'
import advanceImg from 'assets/images/advance-modal.png'
import { LOAN_BANNER_STEPS } from 'constants/wording'

// Components
import PageWrapper from 'components/PageWrapper'
import { NewCardButton } from 'components/CardButtons'
import PrimaryButton from 'components/primaryButton'
import { ProgressBar } from 'components/AdvanceOffer'
import TickIcon from 'components/listbox/SelectedIcon'
import DepletedInterestFreeAdvances from './components/DepletedInterestFreeAdvances'
import DepletedLineModal from '../../components/LineModals/DepletedLineModal'
import FirstTimeInLoanSection from './components/FirstTimeInLoanSection'

const { WALLET, LOANS } = CREDIT_PRODUCT

const ScoringError = () => {
  const [advanceData, setAdvanceData] = useState<LineSummary>(null)
  const [hasAvailableLoan, setHasAvailableLoan] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const errorRoutes = {
    errorMaxAmount: 'error-monto-maximo',
    errorDelayedLoan: 'error-prestamo-demorado',
    errorLoanDebt: 'error-sin-oferta',
    errorInterestFreeMaxAmount: 'error-monto-maximo-tasa-0'
  }

  const { result } = useParams()

  useEffect(() => {
    Promise.all([getCreditLinesSummary(WALLET), getCreditLinesSummary(LOANS)])
      .then(([advanceSummary, loanSummary]) => {
        setAdvanceData(advanceSummary)
        setHasAvailableLoan(
          !loanSummary.isValid ||
            (loanSummary.isValid && loanSummary.remainingAmount)
        )
      })
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    switch (result) {
      case errorRoutes.errorMaxAmount:
        trackEvent({ eventName: 'bille_adelantos_linea_agotada' })
        break
      case errorRoutes.errorDelayedLoan:
        trackEvent({ eventName: 'bille_adelantos_demorado' })
        break
      case errorRoutes.errorLoanDebt:
        trackEvent({ eventName: 'bille_adelantos_rechazo_nosis' })
        break
    }
  }, [])

  return (
    <PageWrapper title='Pedir adelanto' viewSelected='/wayni-credito' noTabBar>
      <section className='flex text-black justify-center items-center mx-auto mt-4 mb-24 sm:max-w-[22.5rem]'>
        {isLoading ? (
          <Skeleton className='w-full' height={100} />
        ) : (
          <>
            {result === errorRoutes.errorMaxAmount && (
              <ErrorMaximumAmount
                advanceApprovedAmount={advanceData.approvedAmount}
                hasAvailableLoanLine={hasAvailableLoan}
              />
            )}
            {result === errorRoutes.errorDelayedLoan && <ErrorDelayedLoan />}
            {result === errorRoutes.errorLoanDebt && <ErrorLoanInDebt />}
            {result === errorRoutes.errorInterestFreeMaxAmount && (
              <DepletedInterestFreeAdvances
                expirationDate={advanceData.expiresAt}
              />
            )}
          </>
        )}
      </section>
    </PageWrapper>
  )
}

export default ScoringError

const ErrorMaximumAmount = ({
  advanceApprovedAmount,
  hasAvailableLoanLine
}: {
  advanceApprovedAmount: number
  hasAvailableLoanLine: boolean
}) => {
  const [depletedModalIsOpen, setDepletedModalIsOpen] = useState(false)
  const [loanModalIsOpen, setLoanModalIsOpen] = useState(false)
  const { user } = useContext(UserContext)

  return (
    <div className='flex flex-col items-center justify-center text-center'>
      <div className='bg-white rounded-[10px] w-full p-4'>
        <div className='border text-xs border-red-900 p-4 rounded-lg'>
          <ErrorIcon className='w-10 h-10 mx-auto' />
          <h1 className='text-black font-bold my-2 leading-5'>
            Agotaste tu línea de crédito
          </h1>
          <p className='text-gray-900 font-medium pb-2 leading-[14px]'>
            Recordá que para pedir un adelanto, tenés que tener tus cuotas al
            día
          </p>
        </div>
        <div className='mt-6 mb-4'>
          <div className='flex w-full justify-between items-center'>
            <p className='text-2xs font-bold'>Adelantos</p>
            <button type='button' onClick={() => setDepletedModalIsOpen(true)}>
              <span className='sr-only'>Saber más</span>
              <HelpIcon
                variant='GREEN'
                className=' h-[1.375rem] w-[1.375rem] mx-1'
              />
            </button>
          </div>
          <ProgressBar
            bgClassName='bg-green-900'
            className='mb-2 mt-3'
            value={1}
            maxValue={1}
          />
          <p className='text-left font-medium text-[10px]'>
            ${toLocaleCurrency(advanceApprovedAmount)} utilizados de $
            {toLocaleCurrency(advanceApprovedAmount)} disponibles
          </p>
        </div>
      </div>

      {hasAvailableLoanLine ? (
        <div className='bg-blue-100 mt-8 relative p-4 text-black rounded-lg'>
          <h2 className='text-left font-bold px-2 text-xl leading-5'>
            Comprá eso que querés con un préstamo a la mejor tasa
          </h2>
          <div className='flex justify-between'>
            <div className='space-y-2 mt-2 text-black text-xs text-left font-normal px-2 self-start'>
              {LOAN_BANNER_STEPS.map((item) => (
                <div key={item.number} className='flex gap-2 items-center '>
                  <TickIcon className='w-4 h-4' />
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
            <img
              className='max-w-32 absolute right-2 top-7 h-auto transform rotate-6'
              src={advanceImg}
              alt=''
            />
          </div>
          <div className='flex gap-3 mt-3'>
            <PrimaryButton
              className='max-w-24 h-9 text-xs hover:bg-purple-900 hover:text-white'
              buttonText='¡Pedir!'
              primaryButtonFunction={() => {
                window
                  .open(
                    `${URL_RENOVADOR}iniciando?access_token=${user.access_token}`,
                    '_blank'
                  )
                  .focus()
              }}
            />
            <button
              type='button'
              className='w-24 h-9 rounded-full bg-transparent text-xs text-black border font-bold border-purple-900'
              onClick={() => setLoanModalIsOpen(true)}
            >
              Más info
            </button>
          </div>
        </div>
      ) : null}
      <FirstTimeInLoanSection
        isOpen={loanModalIsOpen}
        setHasVisited={() => setLoanModalIsOpen(false)}
      />
      <DepletedLineModal
        isOpen={depletedModalIsOpen}
        setIsOpen={setDepletedModalIsOpen}
      />
    </div>
  )
}

const ErrorDelayedLoan = () => {
  const { me } = useContext(MeContext)
  return (
    <div className='flex flex-col items-center justify-center text-center mt-8'>
      <img src={delayedLoan} alt='' />
      <h1 className='text-black text-xl font-bold mt-6 mb-2 leading-5'>
        ¡Tenés un préstamo con cuotas demoradas!
      </h1>
      <p className='text-base text-black font-medium'>
        Pagá tu deuda, volvé y pedí tu adelanto.
      </p>

      <div className='mt-6 w-full'>
        <PrimaryButton
          buttonText='Pagar'
          primaryButtonFunction={() => {
            trackEvent({ eventName: 'bille_adelantos_boton_pagar_demorado' })
            window
              .open(
                `${URL_AUTOGESTION}${me.profile.identification_number}/pagar`,
                '_blank'
              )
              .focus()
          }}
        />
      </div>
    </div>
  )
}

const ErrorLoanInDebt = () => {
  const { me } = useContext(MeContext)
  const navigate = useNavigate()

  if (!me) return null

  return (
    <div className='flex flex-col items-center justify-center text-center'>
      <img alt='' src={notLendMoneyImg} />
      <h1 className='text-black text-xl font-bold mt-6 mb-2'>
        Actualmente no tenemos una oferta para brindarte
      </h1>
      <p className='text-base text-black font-medium'>
        Pagá tus deudas en otros lugares, mejorá tu perfil crediticio y volvé a
        intentarlo.
      </p>

      {hasActiveMembership(me.subscriber_products) && (
        <NewCardButton onClick={() => navigate('/perfil-crediticio')}>
          <>
            <p className='font-bold text-purple-900'>
              Conocé tu perfil crediticio
            </p>
            <p className='text-medium text-2xs text-black'>
              Verificá tus deudas con otras empresas
            </p>
          </>
        </NewCardButton>
      )}

      <PrimaryButton
        className='mt-8'
        buttonText='Volver atrás'
        primaryButtonFunction={() => {
          trackEvent({
            eventName: 'bille_adelantos_boton_volver_rechazo_nosis'
          })
          navigate('/home')
        }}
      />
    </div>
  )
}
